'use client'

import * as React from 'react'
import { AppSwitcherIcon as AppSwitcherDsIcon } from '@/src/icons'
import { cn } from '@/registry/reapit/lib/utils'
import {
  ReapitPMIcon,
  ReapitPMDisabledIcon,
  ReapitLettingsIcon,
  ReapitLettingsDisabledIcon,
  ReapitSalesIcon,
  ReapitSalesDisabledIcon,
  KeyWhereIcon,
  KeyWhereDisabledIcon,
  LettingsBDMIcon,
  LettingsBDMDisabledIcon,
  MoveMeInIcon,
  MoveMeInDisabledIcon,
  ReapitFormsIcon,
  ReapitFormsDisabledIcon,
  ReapitProposalsIcon,
  ReapitProposalsDisabledIcon,
  ReapitWebsitesIcon,
  ReapitWebsitesDisabledIcon,
  AutoResponderIcon,
  AutoResponderDisabledIcon,
} from '@/registry/reapit/ui/app-avatar-icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/registry/reapit/ui/dropdown-menu'
import { TopBarNavIconItemButton } from '@/registry/reapit/ui/top-bar'

// ---------------------------------------------------------------------------
// Product configuration — matches v5 exactly
// ---------------------------------------------------------------------------

/**
 * Reapit Connect product IDs supported by the App Switcher.
 */
type SupportedProductId =
  | 'agentBox'
  | 'autoResponder'
  | 'consoleCloud'
  | 'ireBdm'
  | 'ireWeb'
  | 'keyWhere'
  | 'mmiWeb'
  | 'reapitForms'
  | 'reapitProposals'
  | 'reapitWebsites'

interface ProductConfig {
  appName: string
  supplementaryInfo: string
}

const productConfigs: Record<SupportedProductId, ProductConfig> = {
  autoResponder: {
    appName: 'Auto Responder',
    supplementaryInfo: 'Auto-enquiry follow up',
  },
  agentBox: { appName: 'Reapit Sales', supplementaryInfo: 'Real estate CRM' },
  consoleCloud: { appName: 'Reapit PM', supplementaryInfo: 'Property management' },
  ireBdm: {
    appName: 'Lettings BDM',
    supplementaryInfo: 'Business development',
  },
  ireWeb: {
    appName: 'Reapit Lettings',
    supplementaryInfo: 'Leasing management',
  },
  keyWhere: {
    appName: 'KeyWhere',
    supplementaryInfo: 'Key management',
  },
  mmiWeb: {
    appName: 'Move Me In',
    supplementaryInfo: 'Utility connections service',
  },
  reapitForms: {
    appName: 'Reapit Forms',
    supplementaryInfo: 'Documents & digital signing',
  },
  reapitWebsites: {
    appName: 'Reapit Websites',
    supplementaryInfo: 'Website builder',
  },
  reapitProposals: {
    appName: 'Reapit Proposals',
    supplementaryInfo: 'Interactive digital proposals',
  },
}

/**
 * Display order for products in the App Switcher. Only products in this list
 * are displayed — other product IDs are silently ignored.
 */
const productDisplayOrder: SupportedProductId[] = ['ireWeb', 'consoleCloud', 'agentBox', 'keyWhere', 'ireBdm', 'mmiWeb']

// ---------------------------------------------------------------------------
// Utility functions — match v5 API exactly
// ---------------------------------------------------------------------------

function isProductAccessible(productId: SupportedProductId, accessibleProductIds: string[]) {
  for (const accessibleProductId of accessibleProductIds) {
    const regex = new RegExp(`^${accessibleProductId}$`, 'i')
    if (regex.test(productId)) {
      return true
    }
  }
  return false
}

function getDisplayableProductsForYourAppsGroup(accessibleProductIds: string[]): SupportedProductId[] {
  return productDisplayOrder.filter((productId) => isProductAccessible(productId, accessibleProductIds))
}

function getDisplayableProductsForExploreGroup(accessibleProductIds: string[]): SupportedProductId[] {
  return productDisplayOrder.filter((productId) => !isProductAccessible(productId, accessibleProductIds))
}

// ---------------------------------------------------------------------------
// HasAccess context — determines whether app avatars render in colour
// ---------------------------------------------------------------------------

const MenuGroupHasAccessContext = React.createContext<boolean>(false)

function useMenuGroupHasAccessContext() {
  return React.useContext(MenuGroupHasAccessContext)
}

// ---------------------------------------------------------------------------
// AppSwitcher.AppAvatar — product icon/mark
// ---------------------------------------------------------------------------

export namespace AppSwitcherAppAvatar {
  export interface Props {
    /** Whether the current user has access to this product. */
    hasAccess: boolean
    /** The product whose avatar to render. */
    productId: SupportedProductId
  }
}

/**
 * Product avatar/mark for the App Switcher. Renders the actual brand SVG
 * for each product, with accessible (full colour) and inaccessible (greyed)
 * variants. Ported from Elements v5.
 */
function AppSwitcherAppAvatar({ hasAccess, productId }: AppSwitcherAppAvatar.Props) {
  switch (productId) {
    case 'agentBox':
      return hasAccess ? <ReapitSalesIcon /> : <ReapitSalesDisabledIcon />
    case 'autoResponder':
      return hasAccess ? <AutoResponderIcon /> : <AutoResponderDisabledIcon />
    case 'consoleCloud':
      return hasAccess ? <ReapitPMIcon /> : <ReapitPMDisabledIcon />
    case 'ireBdm':
      return hasAccess ? <LettingsBDMIcon /> : <LettingsBDMDisabledIcon />
    case 'ireWeb':
      return hasAccess ? <ReapitLettingsIcon /> : <ReapitLettingsDisabledIcon />
    case 'keyWhere':
      return hasAccess ? <KeyWhereIcon /> : <KeyWhereDisabledIcon />
    case 'mmiWeb':
      return hasAccess ? <MoveMeInIcon /> : <MoveMeInDisabledIcon />
    case 'reapitForms':
      return hasAccess ? <ReapitFormsIcon /> : <ReapitFormsDisabledIcon />
    case 'reapitWebsites':
      return hasAccess ? <ReapitWebsitesIcon /> : <ReapitWebsitesDisabledIcon />
    case 'reapitProposals':
      return hasAccess ? <ReapitProposalsIcon /> : <ReapitProposalsDisabledIcon />
  }
}

// ---------------------------------------------------------------------------
// AppSwitcher.MenuItem — generic menu item
// ---------------------------------------------------------------------------

export namespace AppSwitcherMenuItem {
  export interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
    avatar: React.ReactNode
    appName: string
    href: string
    supplementaryInfo: string
  }
}

function AppSwitcherMenuItem({
  avatar,
  appName,
  href,
  supplementaryInfo,
  className,
  ...rest
}: AppSwitcherMenuItem.Props) {
  return (
    <DropdownMenuItem asChild className={cn('gap-3 py-3 px-3', className)}>
      <a href={href} target="_blank" rel="noreferrer" {...rest}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '2.5rem',
            height: '2.5rem',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          {avatar}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', minWidth: 0 }}>
          <span
            style={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#222b33',
              overflowWrap: 'anywhere',
            }}
          >
            {appName}
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 400,
              color: '#607890',
              overflowWrap: 'anywhere',
            }}
          >
            {supplementaryInfo}
          </span>
        </span>
      </a>
    </DropdownMenuItem>
  )
}

// ---------------------------------------------------------------------------
// AppSwitcher.ProductMenuItem — product-specific menu item
// ---------------------------------------------------------------------------

export namespace AppSwitcherProductMenuItem {
  export interface Props extends Partial<AppSwitcherMenuItem.Props> {
    productId: SupportedProductId
    href: string
  }
}

function AppSwitcherProductMenuItem({
  appName: appNameOverride,
  avatar: avatarOverride,
  href,
  productId,
  supplementaryInfo: supplementaryInfoOverride,
  ...rest
}: AppSwitcherProductMenuItem.Props) {
  const hasAccess = useMenuGroupHasAccessContext()
  const config = productConfigs[productId]
  return (
    <AppSwitcherMenuItem
      {...rest}
      appName={appNameOverride ?? config.appName}
      avatar={avatarOverride ?? <AppSwitcherAppAvatar hasAccess={hasAccess} productId={productId} />}
      href={href}
      supplementaryInfo={supplementaryInfoOverride ?? config.supplementaryInfo}
    />
  )
}

// ---------------------------------------------------------------------------
// AppSwitcher.YourAppsMenuGroup
// ---------------------------------------------------------------------------

export namespace AppSwitcherYourAppsMenuGroup {
  export interface Props {
    children: React.ReactNode
  }
}

function AppSwitcherYourAppsMenuGroup({ children }: AppSwitcherYourAppsMenuGroup.Props) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">YOUR APPS</DropdownMenuLabel>
      <MenuGroupHasAccessContext.Provider value={true}>{children}</MenuGroupHasAccessContext.Provider>
    </DropdownMenuGroup>
  )
}

// ---------------------------------------------------------------------------
// AppSwitcher.ExploreMenuGroup
// ---------------------------------------------------------------------------

export namespace AppSwitcherExploreMenuGroup {
  export interface Props {
    children: React.ReactNode
  }
}

function AppSwitcherExploreMenuGroup({ children }: AppSwitcherExploreMenuGroup.Props) {
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">EXPLORE</DropdownMenuLabel>
      <MenuGroupHasAccessContext.Provider value={false}>{children}</MenuGroupHasAccessContext.Provider>
    </DropdownMenuGroup>
  )
}

// ---------------------------------------------------------------------------
// AppSwitcher root — trigger + dropdown
// ---------------------------------------------------------------------------

export namespace AppSwitcher {
  export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** The menu groups and items. */
    children: React.ReactNode
  }
}

function AppSwitcherRoot({ children, className, ...rest }: AppSwitcher.Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <TopBarNavIconItemButton
          {...rest}
          aria-label="App Switcher"
          icon={<AppSwitcherDsIcon />}
          className={className}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ---------------------------------------------------------------------------
// Compose AppSwitcher namespace
// ---------------------------------------------------------------------------

const AppSwitcher = Object.assign(AppSwitcherRoot, {
  AppAvatar: AppSwitcherAppAvatar,
  Divider: DropdownMenuSeparator,
  ExploreMenuGroup: AppSwitcherExploreMenuGroup,
  MenuItem: AppSwitcherMenuItem,
  ProductMenuItem: AppSwitcherProductMenuItem,
  YourAppsMenuGroup: AppSwitcherYourAppsMenuGroup,
  getDisplayableProductsForYourAppsGroup,
  getDisplayableProductsForExploreGroup,
})

export {
  AppSwitcher,
  AppSwitcherAppAvatar,
  AppSwitcherMenuItem,
  AppSwitcherProductMenuItem,
  AppSwitcherYourAppsMenuGroup,
  AppSwitcherExploreMenuGroup,
  getDisplayableProductsForYourAppsGroup,
  getDisplayableProductsForExploreGroup,
  productConfigs,
  productDisplayOrder,
  isProductAccessible,
}
export type { SupportedProductId, ProductConfig }
